import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { BookingProvider } from "@/context/BookingContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";

const IBNNetwork = lazy(() => import("@/pages/IBNNetwork"));
const Leadership = lazy(() => import("@/pages/Leadership"));
const Sales = lazy(() => import("@/pages/Sales"));
const Speaking = lazy(() => import("@/pages/Speaking"));
const Podcast = lazy(() => import("@/pages/Podcast"));
const PodcastEpisode = lazy(() => import("@/pages/PodcastEpisode"));
const About = lazy(() => import("@/pages/About"));
const ClaritySession = lazy(() => import("@/pages/ClaritySession"));
const Admin = lazy(() => import("@/pages/Admin"));
const BlackSwan = lazy(() => import("@/pages/BlackSwan"));
const Purpose = lazy(() => import("@/pages/Purpose"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const FreeWebinars = lazy(() => import("@/pages/FreeWebinars"));
const WebinarLibrary = lazy(() => import("@/pages/WebinarLibrary"));

function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [hash, pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BookingProvider>
          <ScrollToHash />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/founder-network" element={<IBNNetwork />} />
              <Route path="/business-networking-group" element={<Navigate to="/founder-network" replace />} />
              <Route path="/ibn-network" element={<Navigate to="/founder-network" replace />} />
              <Route path="/leadership-development-course" element={<Leadership />} />
              <Route path="/leadership" element={<Navigate to="/leadership-development-course" replace />} />
              <Route path="/sales-coaching" element={<Sales />} />
              <Route path="/sales" element={<Navigate to="/sales-coaching" replace />} />
              <Route path="/keynote-speaker-intuitive-leadership" element={<Speaking />} />
              <Route path="/speaking" element={<Navigate to="/keynote-speaker-intuitive-leadership" replace />} />
              <Route path="/the-intuitive-leader-podcast" element={<Podcast />} />
              <Route path="/podcast/:slug" element={<PodcastEpisode />} />
              <Route path="/intuitive-leader-podcast" element={<Navigate to="/the-intuitive-leader-podcast" replace />} />
              <Route path="/podcast" element={<Navigate to="/the-intuitive-leader-podcast" replace />} />
              <Route path="/about-maurice-jenkens" element={<About />} />
              <Route path="/about" element={<Navigate to="/about-maurice-jenkens" replace />} />
              <Route path="/clarity-session-with-maurice" element={<ClaritySession />} />
              <Route path="/black-swan-assessment" element={<BlackSwan />} />
              <Route path="/black-swan" element={<Navigate to="/black-swan-assessment" replace />} />
              <Route path="/how-to-find-your-purpose" element={<Purpose />} />
              <Route path="/clarity" element={<Navigate to="/how-to-find-your-purpose" replace />} />
              <Route path="/start" element={<Navigate to="/how-to-find-your-purpose" replace />} />
              <Route path="/get-clarity" element={<Navigate to="/how-to-find-your-purpose" replace />} />
              <Route path="/manual" element={<Navigate to="/" replace />} />
              <Route path="/9-steps" element={<Navigate to="/" replace />} />
              <Route path="/masterclass" element={<Navigate to="/" replace />} />
              <Route path="/contact" element={<Navigate to="/" replace />} />
              <Route path="/new" element={<Navigate to="/" replace />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/intuition" element={<Navigate to="/" replace />} />
              <Route path="/intuitive" element={<Navigate to="/" replace />} />
              <Route path="/community" element={<Navigate to="/founder-network" replace />} />
              <Route path="/salesteams" element={<Navigate to="/sales-coaching" replace />} />
              <Route path="/power-intuitive-leadership" element={<Navigate to="/leadership-development-course" replace />} />
              <Route path="/speakermaurice" element={<Navigate to="/keynote-speaker-intuitive-leadership" replace />} />
              <Route path="/homeold" element={<Navigate to="/" replace />} />
              <Route path="/communityWebinars" element={<Navigate to="/how-to-develop-intuition-in-business" replace />} />
              <Route path="/webinars" element={<Navigate to="/how-to-develop-intuition-in-business" replace />} />
              <Route path="/decision-making" element={<Navigate to="/black-swan-assessment" replace />} />
              <Route path="/trust-my-gut" element={<Navigate to="/black-swan-assessment" replace />} />
              <Route path="/gut-feeling" element={<Navigate to="/black-swan-assessment" replace />} />
              <Route path="/trust" element={<Navigate to="/black-swan-assessment" replace />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/how-to-develop-intuition-in-business" element={<FreeWebinars />} />
              <Route path="/free-webinars" element={<Navigate to="/how-to-develop-intuition-in-business" replace />} />
              <Route path="/webinar-library" element={<WebinarLibrary />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/admin" element={<Suspense fallback={null}><Admin /></Suspense>} />
          </Routes>
        </BookingProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
